import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Copy, LayoutList, Layers, Users } from "lucide-react";

const MODES = [
  {
    id: "all",
    label: "Everything",
    desc: "Lists, cards, labels, dates, and members",
    icon: Layers,
    copyCards: true,
    copyMembers: true,
  },
  {
    id: "cards",
    label: "Lists & cards",
    desc: "All lists and cards, but no members",
    icon: LayoutList,
    copyCards: true,
    copyMembers: false,
  },
  {
    id: "lists_members",
    label: "Lists & members",
    desc: "Column structure and members, but no cards",
    icon: Users,
    copyCards: false,
    copyMembers: true,
  },
  {
    id: "lists",
    label: "Lists only",
    desc: "Just the column structure — no cards, no members",
    icon: Copy,
    copyCards: false,
    copyMembers: false,
  },
];

export default function DuplicateBoardDialog({ open, onOpenChange, board, onDuplicated }) {
  const [mode, setMode] = useState("all");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const m = MODES.find((m) => m.id === mode);
    try {
      const { data } = await api.post(`/boards/${board.id}/duplicate`, {
        copy_members: m.copyMembers,
        copy_cards: m.copyCards,
      });
      toast.success(`"${board.title}" duplicated`);
      onDuplicated && onDuplicated(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to duplicate");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border-zinc-200 dark:border-zinc-800 sm:max-w-md w-[calc(100vw-2rem)]">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-bold tracking-tight text-zinc-950 dark:text-zinc-100 inline-flex items-center gap-2">
            <Copy className="h-5 w-5" /> Duplicate board
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5 mt-2">
          <p className="text-sm text-zinc-500">
            Create a copy of <strong className="text-zinc-900 dark:text-zinc-200">"{board.title}"</strong>.
            The new board will be completely independent.
          </p>

          <div className="space-y-2">
            {MODES.map((m) => {
              const Icon = m.icon;
              const active = mode === m.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setMode(m.id)}
                  className={`w-full flex items-start gap-3 p-3 border text-left transition-colors ${
                    active
                      ? "border-black dark:border-white bg-zinc-50 dark:bg-zinc-900"
                      : "border-zinc-200 dark:border-zinc-700 hover:border-zinc-400 dark:hover:border-zinc-500"
                  }`}
                >
                  <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${active ? "text-zinc-900 dark:text-white" : "text-zinc-400"}`} />
                  <div>
                    <div className={`text-sm font-medium ${active ? "text-zinc-900 dark:text-white" : "text-zinc-700 dark:text-zinc-300"}`}>
                      {m.label}
                    </div>
                    <div className="text-xs text-zinc-500 mt-0.5">{m.desc}</div>
                  </div>
                </button>
              );
            })}
          </div>

          <DialogFooter className="gap-2 flex-row">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="flex-1 sm:flex-none px-5 py-3 sm:py-2.5 text-sm font-medium border border-zinc-200 dark:border-zinc-700 hover:border-black dark:hover:border-white transition-colors text-zinc-700 dark:text-zinc-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 sm:flex-none px-5 py-3 sm:py-2.5 text-sm font-medium bg-black dark:bg-white text-white dark:text-black hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              <Copy className="h-4 w-4" />
              {loading ? "Duplicating..." : "Duplicate"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
